import { initErrorCode } from 'constants/errors';
import { AnalyticsTarget, KeyAstromTarget } from 'enums/AnalyticsTarget';
import { Severity } from 'enums/Severity';
import { SLAEventLabel } from 'enums/SLAEventLabel';
import { SLAEventPhase } from 'enums/SLAEventPhase';
import { StorageKey } from 'enums/StorageKey';
import { WidgetFeature } from 'enums/WidgetFeature';
import { WorkerMessageType } from 'enums/WorkerMessage';
import { alertableActions, closableActions, WsAction } from 'enums/WsAction';
import { BaseWsMessage } from 'interfaces/BaseWsMessage';
import { WidgetParameters } from 'interfaces/WidgetParameters';
import { OutWsAuthMessage } from 'interfaces/WsAuthMessage';
import { DefaultRootState } from 'react-redux';
import { Store } from 'redux';
import { BaseWsRequestCallback, VOService, VoSessionInfo } from 'services/VOService';
import { savePreAuthUnreadCounter, setUnreadCounter } from 'state/chat/chat.actions';
import {
    setAuthed,
    setChannelInfo,
    setChannelInformBanner,
    setConnected,
    setConnectionError,
    setDisconnected,
    setManualReconnect,
    setVoTokenToStore,
} from 'state/connection/connection.actions';
import { selectFeatureEnabled } from 'state/features/features.selectors';
import InfoIcon from 'svg/20/info.svg';
import { _counterLog } from 'utils/_counterLog';
import { createReachGoalActionBy } from 'utils/analytics';
import { DtrumUtils } from 'utils/analytics/dtrumsutils';
import { debounce } from 'utils/decorators';
import { ChatPerfomance, EDtrumPerformanceEvent } from 'utils/dtrum';
import { randomStr } from 'utils/random';
import { onWorkerMessage } from 'utils/worker';

import { services } from '../container';
import { ServiceConfiguration } from '../ServiceConfiguration';

interface KerberosAnswer {
    ldapId: string;
    token: string;
}

const defaultKerberosAnswer: KerberosAnswer = {
    ldapId: '',
    token: '',
};

export class VOServiceImpl implements VOService {
    private connected = false;
    private authed = false;

    private origin: string = null;
    private apiUrl: string = null;
    private initUrl: string = null;
    private wsUrl: string = null;
    private fileStorageUrl: string = null;
    private filesUrl: string = null;
    private fileUploadUrl: string = null;
    private sendJwtToVoUrl: string = null;
    private unansweredPing = {
        count: 0,
        maxCount: 3,
    };

    private networkParamNames: (keyof WidgetParameters)[] = [
        'host',
        'project',
        'channel',
        'secure',
        'getTokensData',
        'unc',
        'getClientDataJws',
        'clientDataJws',
        'userID',
        'adAuthUrl',
        'adAuthXSource',
        'dontStoreToken',
    ];

    private curReconnectAttempt = 0;
    private showErrorAfterAttempt = 2;
    private maxReconnectAttempts = 5;
    private baseReconnectAttemptDelay = 2; // Seconds, doubles every reconnect attempt
    private reconnectHandle: ReturnType<typeof setTimeout> = null;
    private connectionTookTooLongHandle: ReturnType<typeof setTimeout> = null;

    private widgetStore: Store<DefaultRootState>;
    private setNetworkTimeDiff: (diff: number) => void;
    private widgetParameters: WidgetParameters = null;

    private socket: WebSocket = null;
    private wsTimeout = 30; // Seconds
    private jwtCheckInterval = 1; // Seconds
    private waitForJwtPresenceTimeout = 20; // Seconds
    private lastJwtSentToVo: string = null;
    private wsSessionId = 0;

    // Мапа uuid - callback запроса. Где key (uuid) - Идентификатор сообщения. Используется в ответе для корреляции запроса / ответа
    private wsRequestCallbacks: Map<string, BaseWsRequestCallback> = new Map<
        string,
        BaseWsRequestCallback
    >();

    private tokenStorageKey: string = null;
    private tokenGetter = () => {
        let token = localStorage.getItem(this.tokenStorageKey);

        if (!token && this.widgetStore?.getState) {
            token = this.widgetStore.getState().connection.token;
        }

        return token;
    };
    private tokenSetter = (token: string) => {
        localStorage.setItem(this.tokenStorageKey, token);

        if (this.widgetStore?.dispatch) {
            this.widgetStore.dispatch(setVoTokenToStore(token));
        }
    };

    configure({ widgetStore, setNetworkTimeDiff }: ServiceConfiguration): void {
        this.widgetStore = widgetStore;
        this.setNetworkTimeDiff = setNetworkTimeDiff;

        this.listenConnectionParameters();

        onWorkerMessage(WorkerMessageType.Ping, () => this.ping());
        onWorkerMessage(WorkerMessageType.CheckJwt, () => this.sendJwtToVO());
    }

    public getHostStorageKey(name: string) {
        return `widget.channel_${this.widgetParameters?.channel}.${name}[${this.origin}]`;
    }

    public getStoredToken() {
        return this.tokenGetter();
    }

    public setStoredToken(token: string) {
        this.tokenSetter(token);
    }

    public async forceReconnect() {
        await this.connect(true);
    }

    private listenConnectionParameters() {
        const store = this.widgetStore;
        let curState = store.getState().parameters;

        store.subscribe(() => {
            const newState = store.getState().parameters;

            if (curState === newState) return;

            for (const paramName of this.networkParamNames) {
                if (curState[paramName] !== newState[paramName]) {
                    this.widgetParameters = newState;
                    curState = newState;
                    this.onConnectionParametersChanged();

                    return;
                }
            }
        });

        this.widgetParameters = curState;

        this.onConnectionParametersChanged();
    }

    private onConnectionParametersChanged() {
        services.log.info('Widget connection parameters changed');

        const { secure, host, dontStoreToken, MfPerfomanceDtrum } = this.widgetParameters;

        if (dontStoreToken) {
            let storedToken: string;

            this.tokenGetter = () => storedToken;
            this.tokenSetter = (token: string) => {
                storedToken = token;
            };
        }

        if (MfPerfomanceDtrum) ChatPerfomance.setMfDtrumPerf(MfPerfomanceDtrum);

        this.origin = `http${secure ? 's' : ''}://${host}`;
        this.apiUrl = `${this.origin}/webChat`;
        this.wsUrl = `${this.apiUrl.replace('http', 'ws')}/WebSocket`;
        this.initUrl = `${this.apiUrl}/init`;
        this.fileStorageUrl = `${this.origin}/fileStorage`;
        this.filesUrl = `${this.fileStorageUrl}/files`;
        this.fileUploadUrl = `${this.fileStorageUrl}/fileUpload`;
        this.sendJwtToVoUrl = `${this.apiUrl}/vtb-online/token`;

        this.tokenStorageKey = this.getHostStorageKey(StorageKey.VoToken);

        this.forceReconnect();
    }

    private resetReconnectAttempts = debounce(() => {
        this.curReconnectAttempt = 0;
    }, (this.wsTimeout + 10) * 1000);

    private resetInfromationBanner = () => {
        this.widgetStore.dispatch(setChannelInformBanner(null));
    };

    private async waitForJwtPresence(
        getTokensData: WidgetParameters['getTokensData'],
    ): Promise<string> {
        // Wait until JWT present, checking if so every 0.5 seconds
        // Rejects due to timeout after this.waitForJwtPresenceTimeout seconds
        return new Promise((res, rej) => {
            const jwtCheckHandle = setInterval(() => {
                const jwt = getTokensData()?.idToken;
                if (jwt) {
                    clearInterval(jwtCheckHandle);
                    res(jwt);
                }
            }, 500);
            setTimeout(() => {
                clearInterval(jwtCheckHandle);
                rej(`JWT wasn't obtained after ${this.waitForJwtPresenceTimeout} seconds`);
            }, this.waitForJwtPresenceTimeout * 1000);
        });
    }

    public async connect(manual = false) {
        this.wsSessionId++;

        clearTimeout(this.reconnectHandle);

        this.connectionTookTooLongHandle = setTimeout(() => {
            services.analytics.send(
                createReachGoalActionBy(AnalyticsTarget.CONNECTION_TOOK_TO_LONG),
            );
        }, 5000);

        services.analytics.sendAnalyticsSLAEvent(SLAEventLabel.ChatConnection, SLAEventPhase.Start);

        this.widgetStore.dispatch(setManualReconnect(false));
        this.resetInfromationBanner();

        if (manual) {
            this.curReconnectAttempt = 0;
        }

        if (this.curReconnectAttempt === 0) {
            this.reportErrorIfNeeded(null);
        }

        try {
            this.setConnected(false);

            const { getTokensData } = this.widgetParameters;
            if (getTokensData) {
                await this.waitForJwtPresence(getTokensData);
            }

            const voSessionInfo = await this.voAuth();
            if (voSessionInfo.token) {
                let kerberosAnswer = defaultKerberosAnswer;
                try {
                    if (selectFeatureEnabled(this.widgetStore.getState(), WidgetFeature.AdAuth)) {
                        kerberosAnswer = await this.adAuth(voSessionInfo.token);
                    }
                } catch (err) {
                    services.log.error(`AD-authentication error HTTP ${err}`);
                }

                await this.unreadCounterRequest(kerberosAnswer.ldapId);

                try {
                    if (!this.authed || (await this.wsAuth())) {
                        services.log.info(
                            `Successfully established connection to VO`,
                            Severity.High,
                        );

                        this.setConnected(true);
                        this.setDisconnected(false);
                        this.resetReconnectAttempts();

                        this.widgetStore.dispatch(setChannelInfo(voSessionInfo.channel_info));
                        this.setNetworkTimeDiff?.(
                            Date.now() - new Date(voSessionInfo.date_time).valueOf(),
                        );

                        return true;
                    }
                } catch (err) {
                    const message = `Failed to establish a WS connection: ${String(err)}`;
                    services.log.error(message);
                    this.onError(message);
                }
            } else {
                const message = `Failed to obtain VO token`;
                services.log.error(message);
                this.onError(message);
            }
        } catch (err) {
            const message = `VO connection attempt failed: ${String(err)}`;
            services.log.error(message);
            this.onError(message);
        } finally {
            clearTimeout(this.connectionTookTooLongHandle);
        }

        return false;
    }

    private async adAuth(token: string): Promise<KerberosAnswer> {
        const url = this.widgetStore.getState().parameters.adAuthUrl;
        const xSource = this.widgetStore.getState().parameters.adAuthXSource;

        if (!url || !xSource) {
            services.log.warn(
                'adAuthUrl and adAuthXSource params are required when AD auth is used',
            );
            return;
        }

        const OK_STATUS = 200;

        try {
            const data: Response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Source': xSource,
                },
                body: JSON.stringify({
                    token,
                }),
                credentials: 'include',
            });
            if (data.status !== OK_STATUS) {
                throw new Error(`${data.status}`);
            }
            const body = await data.json();
            return { ...defaultKerberosAnswer, ...body };
        } catch (err) {
            return Promise.reject(err.message);
        }
    }

    public async wsTransmit(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
        if (!this.socket) {
            await this.wsConnect();
        }

        try {
            this.socket.send(data);
        } catch (err) {
            services.log.error(`Failed to send a WS message: ${String(err)}`);
        }

        services.log.info(`WS sent: ${data}`, Severity.VeryLow);
    }

    public async wsSend(
        req: BaseWsMessage,
        sendSessionData = false,
        transform?: (message: BaseWsMessage) => BaseWsMessage,
    ) {
        let uuid: string;
        do {
            uuid = randomStr();
        } while (this.wsRequestCallbacks.has(uuid));

        let msg: BaseWsMessage = { ...req, uuid };
        if (sendSessionData) {
            msg.token = this.getStoredToken();
            msg.projectId = this.widgetParameters.project;
            msg.messengerType = this.widgetParameters.channel;
        }
        if (typeof transform === 'function') {
            msg = transform(msg);
        }

        const data = JSON.stringify(msg);

        await this.wsTransmit(data);

        return uuid;
    }

    public async wsRequest(
        req: BaseWsMessage,
        sendSessionData = false,
        timeout = this.wsTimeout,
        signal?: AbortSignal,
        transform?: (message: BaseWsMessage) => BaseWsMessage,
        closeWsOnFail?: boolean,
    ) {
        const uuid = await this.wsSend(req, sendSessionData, transform);

        return new Promise<BaseWsMessage>((resolve, reject) => {
            signal?.addEventListener('abort', () => {
                reject(`WS Request (${uuid}) was aborted.`);
            });

            const reqSessionId = this.wsSessionId;

            const rejectTimeout = setTimeout(() => {
                this.wsRequestCallbacks.delete(uuid);

                const err = `WebSocket request timed out after ${timeout} seconds. Request body: ${JSON.stringify(
                    req,
                )}`;

                // Only cose the socket for requests that failed in the current WS session.
                if (reqSessionId === this.wsSessionId) {
                    const reconnectOnTimeout = selectFeatureEnabled(
                        this.widgetStore.getState(),
                        WidgetFeature.ReconnectOnTimeout,
                    );
                    if (reconnectOnTimeout) {
                        const isClosable = closableActions.includes(req.action);
                        const isAlertable = alertableActions.includes(req.action);

                        if (isClosable || closeWsOnFail) this.socket?.close();

                        if (isAlertable)
                            services.widget.eventChannel.emit('toast', false, {
                                id: randomStr(),
                                message: `Не удалось загрузить данные`,
                                description: 'Попробуйте еще раз',
                                type: 'warning',
                                icon: InfoIcon,
                            });
                    } else {
                        if (closeWsOnFail) this.socket?.close();
                    }
                    services.log.error(err);
                } else {
                    services.log.warn(err);
                }

                reject(err);
            }, timeout * 1000);

            this.wsRequestCallbacks.set(uuid, (res) => {
                clearTimeout(rejectTimeout);
                this.wsRequestCallbacks.delete(uuid);

                if (res.errors) {
                    const [codeStr, debugMsg] = Object.entries(res.errors)[0];
                    const code = parseInt(codeStr);
                    const { sub_code: subCode, trace_id } = res;

                    services.log.error(
                        `WebSocket request failed with the following error (code: ${code}, sub_code: ${subCode}, trace_id: ${trace_id}): ${debugMsg}`,
                    );

                    this.reportErrorIfNeeded(code, subCode);
                    reject(res);
                } else {
                    resolve(res);
                }
            });
        });
    }

    public async totalMessageCountRequest() {
        const counter = await this.wsRequest({
            action: WsAction.TotalMessageCountRequest,
        })
            .then((res) => res?.total_message_count as string)
            .catch(() => '1000');

        return counter != null ? parseInt(counter) : null;
    }

    public getFileUrl(fileName: string, scope?: string) {
        if (!fileName) return undefined;

        // Return absolute URLs as is, transform to fileStorage URLs otherwise
        if (/^https?:\/\//i.test(fileName)) {
            return fileName;
        }

        let path: string;
        switch (scope) {
            case 'thumb': {
                path = '/thumb/';
                break;
            }
            default: {
                path = '/';
                break;
            }
        }

        return `${this.filesUrl}${path}${fileName}`;
    }

    public getThumbUrl(fileName: string) {
        return this.getFileUrl(fileName, 'thumb');
    }

    public async uploadFile(
        file: Blob,
        fileName: string,
        uuid: string,
        onProgress?: (percent: number) => void,
        aborterCallback?: (abort: () => void) => void,
    ) {
        const body = new FormData();

        body.append('file', file, fileName);
        body.append('token', this.getStoredToken());
        body.append('projectId', this.widgetParameters.project.toString());
        body.append('messengerType', this.widgetParameters.channel.toString());

        const req = new XMLHttpRequest();
        req.withCredentials = true;
        req.open('POST', this.fileUploadUrl);

        aborterCallback?.(() => req.abort());

        return new Promise((res, rej) => {
            req.upload.onprogress = (e) => {
                if (!this.widgetStore.getState().files.uploads[uuid]) {
                    return req.abort();
                }
                if (onProgress) {
                    onProgress(e.loaded / e.total);
                }
            };
            req.onloadend = () => {
                if (req.status === 200) {
                    res(JSON.parse(req.responseText));
                } else {
                    rej(`Failed to upload file: ${req.responseText}`);
                }
            };
            req.send(body);
        });
    }

    private setConnected(connected: boolean) {
        this.connected = connected;
        this.widgetStore.dispatch(setConnected(connected));

        if (!connected) {
            this.setAuthed(false);
        }
    }

    private setDisconnected(disconnected: boolean) {
        this.widgetStore.dispatch(setDisconnected(disconnected));
    }

    private setAuthed(authed: boolean) {
        this.authed = authed;
        this.widgetStore.dispatch(setAuthed(authed));
    }

    private ping() {
        if (this.connected && this.socket?.readyState === WebSocket.OPEN) {
            this.wsRequest(
                {
                    action: WsAction.Ping,
                    uuid: randomStr(),
                },
                undefined,
                undefined,
                undefined,
                undefined,
                false,
            )
                .then(() => {
                    this.unansweredPing.count = 0;
                })
                .catch(() => {
                    const message = 'Server have not responde to ping';
                    this.unansweredPing.count++;
                    if (this.unansweredPing.count >= this.unansweredPing.maxCount) {
                        this.unansweredPing.count = 0;
                        this.onError(message);
                    }
                });
        }
    }

    private async voAuth() {
        const { project, channel } = this.widgetParameters;

        const storedToken = this.getStoredToken();
        const body = new URLSearchParams();

        body.append('projectId', project.toString());
        body.append('messengerType', channel.toString());
        if (storedToken) {
            body.append('token', storedToken);
        }

        let res: Response = null;
        let voSessionInfo: VoSessionInfo = {};
        const postInitStart = performance.now();

        try {
            res = await fetch(this.initUrl, {
                method: 'POST',
                body,
                credentials: 'include',
            });

            ChatPerfomance.addProperties(
                EDtrumPerformanceEvent.INIT,
                performance.now() - postInitStart,
            );

            if (res?.ok) {
                voSessionInfo = await res.json();

                if (!voSessionInfo.date_time) {
                    voSessionInfo.date_time = new Date().toISOString();
                }

                if (!voSessionInfo.token) {
                    this.reportErrorIfNeeded(initErrorCode, 1);
                }
            } else {
                let subCode: number;
                if (res.status >= 400 && res.status < 500) {
                    if (voSessionInfo.error) {
                        subCode = 3;
                    } else {
                        subCode = 2;
                    }
                } else if (res.status >= 500) {
                    subCode = 4;
                }
                this.reportErrorIfNeeded(initErrorCode, subCode);
            }
        } catch (err) {
            services.log.error(`Failed to obtain VO token: ${String(err)}`);
            return null;
        }

        const token = voSessionInfo.token;

        if (token) {
            this.setStoredToken(token);
            services.log.info(`Obtained VO token: ${token}`, Severity.Medium);
        }

        return voSessionInfo;
    }

    private async unreadCounterRequest(ldapId: string) {
        const { unc, userID } = this.widgetParameters;
        const external_id = ldapId || unc || userID;

        if (external_id) {
            const req: BaseWsMessage = {
                action: WsAction.UnreadCounterRequest,
                external_id,
            };

            if (unc) {
                req.systemCode = 'UNC';
            }

            const counter = await this.wsRequest(req, true)
                .then((res) => (res?.data?.unread_counter as number) ?? 0)
                .catch(() => 0);

            if (counter) {
                _counterLog.log(`VOServiceImpl.unreadCounterRequest`, `counter => ${counter}`);
                this.widgetStore.dispatch(setUnreadCounter(counter));
            }
        }
    }

    private async sendJwtToVO(force = false) {
        if (!this.authed && !force) {
            return;
        }

        const token = this.getStoredToken();
        const { getTokensData } = this.widgetParameters;
        const externalToken = getTokensData?.()?.idToken ?? null;

        if (token && externalToken && this.lastJwtSentToVo !== externalToken) {
            services.log.info(`JWT change detected, sending the updated JWT to VO`);
            let res: Response = null;

            try {
                res = await fetch(this.sendJwtToVoUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8',
                    },
                    body: JSON.stringify({
                        token,
                        externalToken,
                    }),
                    credentials: 'include',
                });
            } catch (err) {
                const message = `Failed to send JWT to VO: ${String(err)}`;
                services.log.error(message);
                this.onError(message);
            }

            if (res?.ok) {
                this.lastJwtSentToVo = externalToken;
                services.log.info(
                    `Sent JWT ending with '${externalToken.substr(
                        externalToken.length - 16,
                    )}' to VO (token = '${token}')`,
                );
            }
        }
    }

    private async wsConnect() {
        if (this.socket) {
            this.socket.onclose = undefined;
            this.socket.close(1000, 'WebSocket was closed during reconnect');
            services.log.warn('WebSocket was closed during reconnect');
            this.socket = undefined;
        }

        return new Promise((resolve, reject) => {
            const rejectTimeout = setTimeout(() => {
                const err = `WebSocket connection attempt timed out after ${this.wsTimeout} seconds`;
                services.log.warn(err);
                reject(err);
            }, this.wsTimeout * 1000);

            const startConnection = performance.now();
            const socket = new WebSocket(this.wsUrl);
            services.log.info('Connecting to WebSocket');

            this.socket = socket;
            services.widget.socket = socket;

            socket.onmessage = (e) => this.onWsMessage(e);

            socket.onopen = () => {
                clearTimeout(rejectTimeout);
                services.log.info(`Connected to WebSocket`, Severity.Medium);

                this.socket.onclose = () => {
                    const message = 'WebSocket connection closed';
                    services.log.error(message);
                    this.onError(message);
                };

                this.socket.onerror = (e) => {
                    services.log.error(e.toString());
                };

                ChatPerfomance.addProperties(
                    EDtrumPerformanceEvent.WS,
                    performance.now() - startConnection,
                );

                resolve(socket);
            };

            socket.addEventListener('error', () => {
                const err = 'Failed to connect to WebSocket';
                services.log.error(err);
                this.onError(err);
                reject();
            });
        });
    }

    public async wsAuth() {
        this.setAuthed(false);
        const { getTokensData } = this.widgetParameters;

        const externalToken = getTokensData?.()?.idToken ?? undefined;

        await this.sendJwtToVO(true);

        const clientDataJws = this.widgetParameters.getClientDataJws?.();

        const browserSupportsSharing = services.sharing.doesBrowserSupportSharing();
        const req: OutWsAuthMessage = {
            action: WsAction.Auth,
            device: { sharingSupported: browserSupportsSharing },
        };

        if (externalToken) {
            req.externalToken = externalToken;
        }

        if (clientDataJws) {
            req.clientDataJws = clientDataJws;
            req.client_data = {};

            const { userID } = this.widgetParameters;

            if (userID) {
                req.client_data.external_id = userID;
            }
        }

        this.widgetStore.dispatch(savePreAuthUnreadCounter());

        const socketAuthStart = performance.now();
        const res = await this.wsRequest(req, true).catch((err) => {
            services.log.warn(err);

            return null as BaseWsMessage;
        });
        ChatPerfomance.addProperties(
            EDtrumPerformanceEvent.AUTH,
            performance.now() - socketAuthStart,
        );

        const success = res?.data?.success === 1;

        if (success) {
            services.log.info(`Authorized in WebSocket`, Severity.Medium);
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.SHOW_CONNECTION));
        }

        this.setAuthed(success);

        return success;
    }

    private reportErrorIfNeeded(code?: number, subCode?: number) {
        const errorScreenEnabled = selectFeatureEnabled(
            this.widgetStore.getState(),
            WidgetFeature.ErrorScreen,
        );

        if (
            errorScreenEnabled &&
            (code == null || this.curReconnectAttempt >= this.showErrorAfterAttempt)
        ) {
            this.widgetStore.dispatch(setConnectionError(code, subCode));
        }
    }

    private processIncomingMessage(msg: BaseWsMessage) {
        services.incomingAction.handle(msg, this.getStoredToken());
    }

    private onWsMessage(e: MessageEvent) {
        const data = e.data;

        services.log.info(`WS received: ${data}`, Severity.VeryLow);

        const msg: BaseWsMessage = JSON.parse(data);

        let requestHandle: BaseWsRequestCallback = null;

        if (msg.uuid) {
            requestHandle = this.wsRequestCallbacks.get(msg.uuid);
        }

        if (requestHandle) {
            requestHandle(msg);
        } else {
            this.processIncomingMessage(msg);
        }
    }

    private onError(error?: string) {
        this.setConnected(false);
        this.setDisconnected(true);

        services.log.error(`Connection error occurred`);
        services.analytics.sendAnalyticsSLAEvent(SLAEventLabel.ChatConnection, SLAEventPhase.Error);
        services.analytics.send(createReachGoalActionBy(AnalyticsTarget.SHOW_NO_CONNECTION));
        services.analytics.send(createReachGoalActionBy(AnalyticsTarget.CONNECTION_ERROR, error));

        if (this.socket) {
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            this.socket.close();
            this.socket = null;
            services.log.error(`WebSocket disconnected`);
        }

        if (this.curReconnectAttempt < this.maxReconnectAttempts) {
            DtrumUtils.addCustomError(KeyAstromTarget.BUTTONCLICK_HISTORY_LOAD_DElAY, error);

            const delay =
                this.baseReconnectAttemptDelay * 1000 * Math.pow(2, this.curReconnectAttempt++);

            services.log.info(
                `Reconnecting in ${delay / 1000} seconds (attempt №${this.curReconnectAttempt})...`,
                Severity.Medium,
            );

            this.reconnectHandle = setTimeout(() => {
                services.log.info(`Retrying connection`, Severity.Medium);
                this.connect();
            }, delay);
        } else {
            DtrumUtils.addCustomError(KeyAstromTarget.BUTTONCLICK_HISTORY_LOAD_DElAY, error);
            DtrumUtils.endAction(KeyAstromTarget.BUTTONCLICK_HISTORY_LOAD_DElAY);

            this.widgetStore.dispatch(setManualReconnect(true));

            services.log.error(
                `Automatic reconnect failed after ${this.maxReconnectAttempts} attempts`,
            );
        }
    }
}
